import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Container, Col, Row } from 'react-bootstrap';
import {
  Activity,
  Box,
  ChevronDown,
  CircleHelp,
  Gauge,
  Grid2x2,
  ShieldCheck,
  Users
} from 'lucide-react';
import './UnifiedAnalytics.css';
import systemAnalyticsIcon from '../../assets/icon/systemAnalyticsIcon.svg';
import filterIcon from '../../assets/icon/filterIcon.svg';
import calenderIcon from '../../assets/icon/calenderIcon.svg';
import FilterForm from '../../components/SystemAnalytics/FilterForm';
import UnileverAnimation from '../../components/UnileverAnimation';
import { exportAnalyticsReport } from '../../utilities/analyticsExport';
import { ReactComponent as ExportIcon } from '../../assets/icon/Export.svg';
import { useAzureData } from '../../context/AzureContext';
import { fetchUnifiedDashboardKpis, filterDropdownData, filterDropdownDataUnified } from '../../service/apiServices';
import useCloudProvider from '../../hooks/useCloudProvider';
type KpiItem = {
  metric: string;
  production: number | string | null;
  non_production: number | string | null;
  total?: number | string | null;
  average?: number | string | null;
};

type SummaryBlock = Record<string, any>;
type UnifiedWarning = {
  env?: string;
  message?: string;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const pastDate = new Date();
  pastDate.setDate(today.getDate() - 6);
  return { fromDate: pastDate, toDate: today };
};

const formatDate = (date: Date) => date.toLocaleDateString('en-GB');

const formatValue = (metricName: string, value: any) => {
  if (value === null || value === undefined || value === '') return '-';

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);

  const name = metricName.toLowerCase();
  if (name.includes('success rate') || name.includes('error rate') || name.includes('compliance score')) {
    return `${numeric.toFixed(2)}%`;
  }

  if (name.includes('cost')) {
    const decimals = name.includes('avg cost') || name.includes('per request') || name.includes('per 1k') ? 4 : 2;
    return `EUR ${numeric.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}`;
  }

  if (Math.abs(numeric) >= 1_000_000_000) return `${(numeric / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(numeric) >= 1_000_000) return `${(numeric / 1_000_000).toFixed(1)}M`;
  if (Math.abs(numeric) >= 1_000) return `${(numeric / 1_000).toFixed(1)}K`;

  return Number.isInteger(numeric) ? `${numeric}` : numeric.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const KPI_GROUPS: Array<{ title: string; matchers: RegExp[]; icon: React.ReactNode }> = [
  { title: 'Application Adoption', matchers: [/applications?/i, /unique users/i], icon: <Grid2x2 size={18} /> },
  { title: 'Usage', matchers: [/^total requests?$/i, /^input tokens?$/i, /^output tokens?$/i, /^total tokens?$/i, /^models used$/i, /^rag requests?$/i], icon: <Activity size={18} /> },
  { title: 'Cost Efficiency', matchers: [/cost/i], icon: <Gauge size={18} /> },
  {
    title: 'Reliability and Safety',
    matchers: [/success rate/i, /error rate/i, /compliance score/i, /policy violations?/i, /pii detections?/i, /jailbreak attempts?/i],
    icon: <ShieldCheck size={18} />
  }
];

const groupKpis = (items: KpiItem[]) => {
  return KPI_GROUPS.map((group) => ({
    ...group,
    items: items.filter((kpi) => group.matchers.some((matcher) => matcher.test(kpi.metric)))
  })).filter((group) => group.items.length > 0);
};

const isTokenMetric = (metricName: string) => /^input tokens?$/i.test(metricName) || /^output tokens?$/i.test(metricName) || /^total tokens?$/i.test(metricName);

const isTotalRequestsMetric = (metricName: string) => /^total requests?$/i.test(metricName);

const findKpiByMetric = (items: KpiItem[], metricName: string) => (
  items.find((item) => item.metric.toLowerCase() === metricName.toLowerCase())
);

const truncateText = (text: string, maxLength = 30) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

const MetricCard = ({ metric }: { metric: KpiItem }) => {
  const metricRows = [
    { label: 'Production', value: metric.production, className: 'production' },
    { label: 'Non-Production', value: metric.non_production, className: 'non-production' },
    metric.average !== undefined
      ? { label: 'Average', value: metric.average, className: 'average' }
      : { label: 'Total', value: metric.total, className: 'total' }
  ];
  const metricName = metric.metric.toLowerCase();
  const icon = metricName.includes('application')
    ? <Grid2x2 size={18} />
    : metricName.includes('request')
      ? <Activity size={18} />
      : metricName.includes('token')
        ? <Box size={18} />
        : metricName.includes('user')
          ? <Users size={18} />
          : metricName.includes('rate') || metricName.includes('compliance') || metricName.includes('violation') || metricName.includes('pii') || metricName.includes('jailbreak')
            ? <ShieldCheck size={18} />
            : <Gauge size={18} />;

  return (
    <article className="ua-card">
      <div className="ua-card__top">
        <h3>{metric.metric}</h3>
        <div className="ua-card__icon">
          {icon}
        </div>
      </div>
      <div className="ua-card__values">
        {metricRows.map((row) => (
          <div className="ua-card__row" key={row.label}>
            <span className="ua-card__label">{row.label}</span>
            <span className={`ua-card__value ${row.className}`}>{formatValue(metric.metric, row.value)}</span>
          </div>
        ))}
      </div>
    </article>
  );
};

const TokenBreakdownRow = ({
  label,
  total,
  input,
  output,
  valueClassName,
  defaultOpen = false
}: {
  label: string;
  total: number | string | null | undefined;
  input: number | string | null | undefined;
  output: number | string | null | undefined;
  valueClassName: string;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`ua-token-row ua-token-row--${valueClassName}`}>
      <button
        type="button"
        className="ua-token-row__summary"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <span className="ua-token-row__label">
          {label}
          <ChevronDown size={15} className={`ua-token-row__chevron ${isOpen ? 'open' : ''}`} />
        </span>
        <span className={`ua-card__value ${valueClassName}`}>{formatValue('Total Tokens', total)}</span>
      </button>

      {isOpen && (
        <div className="ua-token-row__details">
          <div>
            <span>Input</span>
            <strong>{formatValue('Input Tokens', input)}</strong>
          </div>
          <div className="ua-token-row__divider" />
          <div>
            <span>Output</span>
            <strong>{formatValue('Output Tokens', output)}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

const TokensConsumedCard = ({
  inputTokens,
  outputTokens,
  totalTokens
}: {
  inputTokens?: KpiItem;
  outputTokens?: KpiItem;
  totalTokens?: KpiItem;
}) => (
  <article className="ua-card ua-token-card">
    <div className="ua-card__top">
      <div className="ua-token-card__title">
        <h3>Tokens Consumed</h3>
        <div className="ua-token-info" tabIndex={0}>
          <CircleHelp size={17} />
          <div className="ua-token-tooltip">
            Under the subscription model, 300 M input tokens and 120 M output tokens are covered; beyond this, usage is pay as you go.
          </div>
        </div>
      </div>
      <div className="ua-card__icon">
        <Box size={18} />
      </div>
    </div>

    <div className="ua-card__values ua-token-card__values">
      <TokenBreakdownRow
        label="Production"
        total={totalTokens?.production}
        input={inputTokens?.production}
        output={outputTokens?.production}
        valueClassName="production"
      />
      <TokenBreakdownRow
        label="Non-Production"
        total={totalTokens?.non_production}
        input={inputTokens?.non_production}
        output={outputTokens?.non_production}
        valueClassName="non-production"
      />
      <div className="ua-card__row ua-token-card__total">
        <span className="ua-card__label">Total</span>
        <span className="ua-card__value total">{formatValue('Total Tokens', totalTokens?.total)}</span>
      </div>
    </div>
  </article>
);

const UnifiedAnalytics: React.FC = () => {
  const { apiToken } = useAzureData();
  const { fromDate, toDate } = getDefaultDateRange();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [chosenFilter, setChosenFilter] = useState<{ env?: string; application?: string; timePeriod?: { from: string; to: string } }>({});
  const hasFetchedRef = useRef(false);
  const [dropdownValuesLoading, setDropdownValuesLoading] = useState(false);
  const [filterDropdownValues, setFilterDropdownvalues] = useState<any>(null);
  const [currentchooseApp, setCurrentChoosedApp] = useState<any>('')
  const { selectedCloud, cloudProvidersList, selectCloud } = useCloudProvider();
  const [cloudFilterID, setCloudFilter] = useState(0);
  const [currentSelectedFilterOfEnv,setCurrentSelectedFilterOfEnv] = useState<string>("");

  const [dateRange, setDateRange] = useState({
    from: formatDate(fromDate),
    to: formatDate(toDate)
  });

  const fetchData = async (appliedFilters: Record<string, any> = {}) => {
    if (!apiToken) return;

    setLoading(true);
    try {
      const response = await fetchUnifiedDashboardKpis(apiToken, appliedFilters);
      if (response) {
        setDashboardData(response);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current || !apiToken) return;
    hasFetchedRef.current = true;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken]);

  const fetchDropdownData = async () => {
    // if (filterDropdownValues) return
    setDropdownValuesLoading(true);
    try {
      let response = await filterDropdownDataUnified(apiToken, currentSelectedFilterOfEnv ? currentSelectedFilterOfEnv : "", selectedCloud ? selectedCloud : 0);
      response && setFilterDropdownvalues(response);
      setDropdownValuesLoading(false);
    } catch (error) {
      setDropdownValuesLoading(false);
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    fetchDropdownData();
  }, [selectedCloud, currentSelectedFilterOfEnv]);

  const kpis: KpiItem[] = useMemo(() => dashboardData?.kpis || [], [dashboardData?.kpis]);
  const groupedKpis = useMemo(() => groupKpis(kpis), [kpis]);
  const tokenMetrics = useMemo(() => ({
    inputTokens: findKpiByMetric(kpis, 'Input Tokens'),
    outputTokens: findKpiByMetric(kpis, 'Output Tokens'),
    totalTokens: findKpiByMetric(kpis, 'Total Tokens')
  }), [kpis]);
  const hasTokensConsumedCard = !!(tokenMetrics.inputTokens || tokenMetrics.outputTokens || tokenMetrics.totalTokens);
  const summary: SummaryBlock = dashboardData?.summary || {};
  const environments: Record<string, SummaryBlock> = dashboardData?.environments || {};
  const warnings: UnifiedWarning[] = dashboardData?.warnings || [];

  let applicationNames = filterDropdownValues && filterDropdownValues?.filters?.applications?.length > 0 && filterDropdownValues?.filters?.applications?.map((item: any) => ({ label: item, value: item }));

  const exportData = () => {
    exportAnalyticsReport({
      fileName: `unified_dashboard_${chosenFilter?.env || 'all_env'}_${chosenFilter?.application || 'all_apps'}`,
      sheets: [
        {
          name: 'Filters',
          rows: [
            { Field: 'Environment', Value: chosenFilter?.env || 'All Environments' },
            { Field: 'Application', Value: chosenFilter?.application || 'All Applications' },
            { Field: 'Date From', Value: chosenFilter?.timePeriod?.from || dateRange.from },
            { Field: 'Date To', Value: chosenFilter?.timePeriod?.to || dateRange.to }
          ]
        },
        {
          name: 'KPIs',
          rows: kpis.map((item) => ({
            Metric: item.metric,
            Production: item.production,
            NonProduction: item.non_production,
            Total: item.total,
            Average: item.average
          }))
        },
        {
          name: 'Production Summary',
          rows: Object.entries(summary?.production || {}).map(([key, value]) => ({
            Field: key,
            Value: value
          }))
        },
        {
          name: 'Non Production Summary',
          rows: Object.entries(summary?.non_production || {}).map(([key, value]) => ({
            Field: key,
            Value: value
          }))
        },
        {
          name: 'Total Summary',
          rows: Object.entries(summary?.total || {}).map(([key, value]) => ({
            Field: key,
            Value: value
          }))
        },
        {
          name: 'Average Summary',
          rows: Object.entries(summary?.average || {}).map(([key, value]) => ({
            Field: key,
            Value: value
          }))
        },
        {
          name: 'Environment Breakout',
          rows: Object.entries(environments).map(([env, values]) => ({
            Environment: env,
            TotalApplications: values?.totalApplications,
            ActiveApplications: values?.activeApplications,
            TotalRequests: values?.totalRequests,
            TotalTokens: values?.totalTokens,
            TotalCostEur: values?.totalCostEur,
            AvgCostPerRequestEur: values?.avgCostPerRequestEur,
            AvgCostPer1kTokensEur: values?.avgCostPer1kTokensEur,
            SuccessRatePct: values?.successRatePct,
            ErrorRatePct: values?.errorRatePct,
            UniqueUsers: values?.uniqueUsers,
            UniqueApplications: values?.uniqueApplications,
            ModelsUsed: values?.modelsUsed,
            RagRequests: values?.ragRequests,
            RagCostEur: values?.ragCostEur,
            ComplianceScorePct: values?.complianceScorePct,
            PolicyViolations: values?.policyViolations,
            PiiDetections: values?.piiDetections,
            JailbreakAttempts: values?.jailbreakAttempts
          }))
        },
        {
          name: 'Warnings',
          rows: warnings.map((warning) => ({
            Environment: warning?.env || '-',
            Message: warning?.message || '-'
          }))
        }
      ].filter((sheet) => sheet.rows && sheet.rows.length > 0)
    });
  };

  return (
    <Container fluid className="p-4 systemAnalytics_container">
      {/* Header */}
      <div className="mb-2">
        <Row className="justify-content-between align-items-start">
          <Col>
            <h2 className="analytics_heading m-0">
              <img src={systemAnalyticsIcon} alt="analytics" /> Unified KPI Dashboard
            </h2>
            <p className="analytics_subheading">
              Production and non-production KPI view across adoption, usage, cost, RAG, and safety.
            </p>
          </Col>
        </Row>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center" style={{ marginTop: '10%' }}>
          {/* <Spinner animation="border" variant="primary" /> */}
          <UnileverAnimation />
        </div>
      ) : (
        <>
          {/* Filter section */}
          <Row className="d-flex mb-4">
            <Col className="d-flex align-align-items-center">
              <div className="d-flex align-items-center show_date_btn me-2">
                {truncateText(chosenFilter?.env || '') || 'All Environments'}
              </div>
              <div className="d-flex align-items-center show_date_btn me-2">
                {truncateText(chosenFilter?.application) || 'All Applications'}
              </div>
              <div className="d-flex align-items-center show_date_btn me-2">
                {['Azure', 'GCP']?.[cloudFilterID - 1] || 'GCP and Azure'}
              </div>
              {chosenFilter && (
                <div className="d-flex align-items-center gap-2 show_date_btn">
                  <img src={calenderIcon} alt="calender" />
                  {dateRange.from} - {dateRange.to}
                </div>
              )}
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                onClick={() => setShow(true)}
                variant="outline-secondary"
                className="d-flex align-items-center ms-2 me-2 gap-2 filterBtn">
                <img src={filterIcon} alt="filter" />
                Filter
              </Button>
              <Button
                onClick={exportData}
                variant="outline-secondary"
                className="d-flex align-items-center gap-2 filterBtn"
                disabled={loading}
              >
                <ExportIcon width={16} height={16} />
                Export
              </Button>
            </Col>
          </Row>

          <div className="ua-shell">
            <div className="ua-sections">
              {groupedKpis.map((group) => (
                <section key={group.title} className="ua-section">
                  <div className="ua-section__header">
                    <h2>{group.title}</h2>
                  </div>
                  <div className="ua-grid ua-grid--4">
                    {group.title === 'Usage' && hasTokensConsumedCard ? (
                      <>
                        {group.items.filter((item) => isTotalRequestsMetric(item.metric)).map((item) => (
                          <MetricCard key={item.metric} metric={item} />
                        ))}
                        <TokensConsumedCard
                          inputTokens={tokenMetrics.inputTokens}
                          outputTokens={tokenMetrics.outputTokens}
                          totalTokens={tokenMetrics.totalTokens}
                        />
                        {group.items.filter((item) => !isTokenMetric(item.metric) && !isTotalRequestsMetric(item.metric)).map((item) => (
                          <MetricCard key={item.metric} metric={item} />
                        ))}
                      </>
                    ) : (
                      group.items.map((item) => (
                        <MetricCard key={item.metric} metric={item} />
                      ))
                    )}
                  </div>
                </section>
              ))}

              {/* {!!warnings.length && (
                <section className="ua-section">
                  <div className="ua-section__header">
                    <h2>Warnings</h2>
                  </div>
                  <div className="ua-warning-box">
                    <ul>
                      {warnings.map((warning, index) => (
                        <li key={`${warning?.env || 'warning'}-${index}`}>
                          {warning?.env ? `${warning.env}: ` : ''}{warning?.message || String(warning)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )} */}

            </div>
          </div>
        </>)
      }

      <FilterForm
        show={show}
        onClose={() => setShow(false)}
        onApply={(filters) => {
          setChosenFilter(filters);
          setCloudFilter(selectedCloud);
          setDateRange({
            from: filters?.timePeriod?.from || dateRange.from,
            to: filters?.timePeriod?.to || dateRange.to
          });
          fetchData({
            env: filters?.env,
            application: filters?.application,
            start_date: filters?.timePeriod?.from,
            end_date: filters?.timePeriod?.to,
            cloud_platform_id: selectedCloud
          });
        }}
        onDateRangeChange={setDateRange}
        onCurrentChoosedAppChange={setCurrentChoosedApp}
        onCurrentChoosedEnvChange={setCurrentSelectedFilterOfEnv}
        dropdownValuesLoading={dropdownValuesLoading}
        showCloudProvider={true}
        selectedCloud={selectedCloud}
        cloudProvidersList={cloudProvidersList}
        selectCloud={selectCloud}
        fields={[
          {
            key: 'env',
            label: 'Environment',
            type: 'select',
            options: [
              { label: 'Production', value: 'prod' },
              { label: 'Non Production', value: 'nonprod' },
              // { label: 'QA', value: 'qa' },
              // { label: 'Development', value: 'dev' }
            ]
          },
          {
            key: 'application',
            label: 'Application',
            type: 'select',
            options: applicationNames ? applicationNames : []
          },
          { key: 'timePeriod', label: 'Time Period', type: 'dateRange' }
        ]}
      />
    </Container>
  );
};

export default UnifiedAnalytics;
